import { PoSelectOption } from '@po-ui/ng-components';

/** Presets de período das telas do Smart Review (fila/painéis de curadoria, custos). */
export type PeriodPreset = 'last7' | 'last30' | 'last90' | 'all' | 'custom';

/** Período resolvido em datas (formato `YYYY-MM-DD`, inclusivo). Vazio = sem recorte. */
export interface Period {
  preset: PeriodPreset;
  dateFrom: string;
  dateTo: string;
}

/** Opções do seletor de período. */
export const PERIOD_PRESET_OPTIONS: PoSelectOption[] = [
  { value: 'last7', label: 'Últimos 7 dias' },
  { value: 'last30', label: 'Últimos 30 dias' },
  { value: 'last90', label: 'Últimos 90 dias' },
  { value: 'all', label: 'Todo o período' },
  { value: 'custom', label: 'Personalizado' }
];

/** Quantidade de dias de cada preset relativo (exclui `all`/`custom`). */
const PRESET_DAYS: Partial<Record<PeriodPreset, number>> = {
  last7: 7,
  last30: 30,
  last90: 90
};

/** Formata uma data local como `YYYY-MM-DD` (sem fuso/UTC shift). */
export function formatPeriodDate(date: Date): string {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/** Resolve um preset relativo (`last7/30/90`) em `{ dateFrom, dateTo }`. */
export function resolvePresetRange(preset: PeriodPreset, today = new Date()): { dateFrom: string; dateTo: string } {
  const days = PRESET_DAYS[preset];
  if (!days) {
    return { dateFrom: '', dateTo: '' };
  }
  const from = new Date(today);
  from.setDate(from.getDate() - (days - 1));
  return { dateFrom: formatPeriodDate(from), dateTo: formatPeriodDate(today) };
}

/** Período inicial padrão (últimos 30 dias) — evita carregar a base inteira. */
export function defaultPeriod(today = new Date()): Period {
  const range = resolvePresetRange('last30', today);
  return { preset: 'last30', ...range };
}

/**
 * Janela imediatamente anterior, de mesma duração — a base de comparação de "quanto
 * variou". Para 01/07 a 30/07 (30 dias), devolve 01/06 a 30/06.
 *
 * Devolve `null` sem período fechado: sem as duas pontas não há duração a espelhar, e
 * comparar contra uma janela arbitrária seria pior que não comparar.
 */
export function previousPeriod(period: Period): { dateFrom: string; dateTo: string } | null {
  if (!period?.dateFrom || !period?.dateTo) { return null; }

  const from = parsePeriodDate(period.dateFrom);
  const to = parsePeriodDate(period.dateTo);
  if (!from || !to || to < from) { return null; }

  const days = Math.round((to.getTime() - from.getTime()) / 86400000) + 1; // inclusivo nas duas pontas
  const previousTo = new Date(from);
  previousTo.setDate(previousTo.getDate() - 1);
  const previousFrom = new Date(previousTo);
  previousFrom.setDate(previousFrom.getDate() - (days - 1));

  return { dateFrom: formatPeriodDate(previousFrom), dateTo: formatPeriodDate(previousTo) };
}

/**
 * Um período guardado no navegador de volta para o estado da tela — ou `null` quando o
 * que estava salvo não descreve um recorte utilizável.
 *
 * Presets relativos (`last7/30/90`) são RE-resolvidos contra hoje, e as datas salvas
 * junto com eles são descartadas: o que o usuário escolheu foi "últimos 30 dias", não a
 * janela de 30 dias daquele dia. Restaurar as datas literais faria a tela abrir semanas
 * depois mostrando um intervalo vencido sob o rótulo "Últimos 30 dias" — números velhos
 * com cara de atuais, que é pior que não ter salvado nada.
 *
 * `custom` é o oposto: ali as datas SÃO a escolha, então voltam como estão — e um
 * `custom` sem as duas pontas é recusado, porque não recorta nada.
 */
export function rehydratePeriod(raw: unknown, today = new Date()): Period | null {
  if (!raw || typeof raw !== 'object') { return null; }

  const value = raw as Partial<Period>;
  const preset = value.preset;
  if (!PERIOD_PRESET_OPTIONS.some(option => option.value === preset)) { return null; }

  if (preset === 'all') { return { preset, dateFrom: '', dateTo: '' }; }

  if (preset === 'custom') {
    const dateFrom = isPeriodDate(value.dateFrom) ? value.dateFrom : '';
    const dateTo = isPeriodDate(value.dateTo) ? value.dateTo : '';
    return dateFrom && dateTo ? { preset, dateFrom, dateTo } : null;
  }

  return { preset: preset as PeriodPreset, ...resolvePresetRange(preset as PeriodPreset, today) };
}

/** True para `YYYY-MM-DD` — a única forma de data que a API e a tela trocam. */
function isPeriodDate(value: unknown): value is string {
  return typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value);
}

/** `YYYY-MM-DD` como data local. `new Date(iso)` seria UTC e recuaria um dia. */
function parsePeriodDate(value: string): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) { return null; }
  return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
}
